import { FC, ReactNode } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { isString } from "lodash";
import { makeSxStyles } from "ui";

export interface InteractableCellProps {
  onDelete?: () => void;
  onLink?: () => void;
  onEdit?: () => void;
  onSend?: () => void;
  onSendDisabled?: boolean;
  onDownload?: () => void;
  label: string | ReactNode;
  onPause?: () => void;
  isPaused?: boolean;
}

const InteractableCell: FC<InteractableCellProps> = (props) => {
  const styles = useStyles();
  const {
    label,
    onEdit,
    onLink,
    onDelete,
    onSend,
    onPause,
    isPaused,
    onSendDisabled,
    onDownload,
  } = props;
  const isInteractable = !!onEdit || !!onDelete || !!onDownload;
  return (
    <Box sx={styles.section}>
      <Box sx={styles.labelContainer}>
        {isString(label) && <Typography variant="caption">{label}</Typography>}
        {!isString(label) && label}
      </Box>
      {isInteractable && (
        <Box sx={styles.buttons} className="toShow ">
          {!!onDownload && (
            <Tooltip title="Download Plow Report">
              <IconButton
                size="small"
                onClick={onDownload}
                data-testid="download-plow-report"
              >
                <SaveAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!!onPause && (
            <IconButton size="small" onClick={onPause}>
              {isPaused ? (
                <PlayCircleOutlineIcon fontSize="small" />
              ) : (
                <PauseCircleOutlineIcon fontSize="small" />
              )}
            </IconButton>
          )}
          {!!onSend && (
            <IconButton disabled={onSendDisabled} size="small" onClick={onSend}>
              <SendIcon fontSize="small" />
            </IconButton>
          )}
          {!!onLink && (
            <IconButton size="small" onClick={onLink}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          )}
          {!!onEdit && (
            <IconButton size="small" onClick={onEdit} data-testid="edit-button">
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {!!onDelete && (
            <IconButton
              size="small"
              onClick={onDelete}
              data-testid="delete-button"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  section: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  labelContainer: {
    flex: 1,
    paddingRight: "8px",
    minWidth: 0, // Allow text to truncate if needed
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0, // Prevent buttons from shrinking
    ".MuiIconButton-root": {
      backgroundColor: "transparent !important",
    },
  },
}));

export default InteractableCell;
